import { isNumberValue, isStringValue } from "@sameday/core/typePredicates";
import { getApiUrl, getNgrokFetchHeaders } from "@utils";
import type { PostHog } from "posthog-js";
import { sanitizeAnalyticsEvent } from "./sanitizeAnalyticsEvent";

type PublicConfig = {
  posthogKey?: string | null;
  posthogHost?: string | null;
};

const DEFAULT_POSTHOG_HOST = "https://us.i.posthog.com";

// The SDK initializes asynchronously (the key is fetched from dale), so consumers
// like usePostHogCompanyGating must be able to re-evaluate once init lands. This
// is a useSyncExternalStore source signalling "init complete".
let posthogReady = false;
const readyListeners = new Set<() => void>();

// The SDK is loaded lazily by bootstrapPostHog (dynamic import) so posthog-js
// stays out of the entry chunk. Null until the SDK has been loaded AND
// initialized with a key.
let posthogInstance: PostHog | null = null;

/**
 * The initialized PostHog client, or null until bootstrapPostHog has loaded
 * and initialized the SDK. Callers must null-guard; before init nothing is
 * captured anyway (the SDK inits opted-out), so skipping a call is a no-op.
 */
export function getPostHog(): PostHog | null {
  return posthogInstance;
}

/** Whether the PostHog SDK has finished initializing. */
export function getPostHogReady(): boolean {
  return posthogReady;
}

/** Subscribe to PostHog readiness; the callback fires once init completes. */
export function subscribePostHogReady(onChange: () => void): () => void {
  readyListeners.add(onChange);
  return () => {
    readyListeners.delete(onChange);
  };
}

/**
 * Initialize PostHog with a project key + host supplied at runtime. We don't
 * bake VITE_PUBLIC_POSTHOG_* into the bundle — production has no web-app build
 * var for it. The public project key lives in dale's Doppler (settings.posthog)
 * and is fetched via bootstrapPostHog below.
 *
 * `posthog` is the SDK's global client, dynamically imported so posthog-js
 * ships as its own lazy chunk instead of in the entry bundle.
 */
export function initPostHog(
  posthog: Pick<PostHog, "init">,
  key: string,
  host: string,
): void {
  posthog.init(key, {
    api_host: host,
    defaults: "2026-01-30",
    // Manual captureException() in the route error boundaries is the intended
    // path. Disable exception autocapture so boundary errors aren't double-
    // ingested and unhandled rejections/console errors don't flood PostHog
    // before triage/suppression exists (owned by sibling analytics tickets).
    capture_exceptions: false,
    // We do not use PostHog Surveys — disable at the SDK level so they're
    // never fetched or rendered, regardless of the project-side toggle.
    disable_surveys: true,
    // Autocapture + rage clicks are already the SDK defaults, but the privacy
    // sign-off (SD-10019) is for this exact stance — state it explicitly so a
    // `defaults` bump can't silently change it. PII exposure is bounded by the
    // before_send scrubber below plus .ph-no-capture on conversation/
    // transcript/customer surfaces (that class also suppresses rage- and
    // dead-click capture inside the subtree).
    autocapture: true,
    rageclick: true,
    // Unset defers to the PostHog project-side toggle. Dead clicks are a core
    // frustration signal for SD-10016 — force ON deterministically.
    capture_dead_clicks: true,
    // Session replay is OFF per privacy sign-off. The SDK default defers to
    // the project-side toggle; pinning it here means a stray flip in the
    // PostHog UI cannot start recording customer conversations.
    disable_session_recording: true,
    // Pageviews are owned by the router-level tracker (pageviewTracker.ts),
    // which reports normalized route patterns after each navigation commits.
    // The SDK default ('history_change' via `defaults`) would double-capture
    // with raw URLs — Mongo IDs fragment page-usage reports and query strings
    // can reference customers.
    capture_pageview: false,
    // The default ('if_capture_pageview') would silently disable pageleave now
    // that SDK pageviews are off — keep it for session-duration/bounce data.
    capture_pageleave: true,
    // Field RUM (ADR 0002): capture Core Web Vitals (LCP/CLS/FCP/INP) as
    // $web_vitals events. They ride the same per-company opt-in gating and
    // before_send scrubbing as every other event. network_timing is pinned OFF
    // (not left to SDK defaults/remote config): it exists to feed session
    // replay, which is disabled above, and request URLs can reference
    // customers.
    capture_performance: { web_vitals: true, network_timing: false },
    // Default OFF: capture nothing until a super explicitly enables PostHog
    // for the active company (usePostHogCompanyGating opts back in). Keeps
    // tracking opt-in per company and silent on public/pre-login routes.
    opt_out_capturing_by_default: true,
    // Redact email/phone/address PII (plus Firebase oobCode/apiKey auth
    // credentials — SD-12435) from every event before it leaves the browser.
    // Autocapture records the *text* of clicked elements (customer names,
    // phones, transcript snippets on Conversations/Coach/Reports) and the full
    // $current_url — neither is masked by the SDK default. before_send scrubs
    // event `properties`; $set/$set_once (identify person props) pass through.
    before_send: sanitizeAnalyticsEvent,
  });
}

/**
 * Fetch the public PostHog config from dale, lazily load the SDK, and
 * initialize it. Runs once at startup; init is async (a beat after load),
 * which is fine because default-opt-out + per-company gating defer all capture
 * until post-login. Degrades silently if the config is unavailable — PostHog
 * just stays off, and the SDK chunk is never even downloaded.
 */
export async function bootstrapPostHog(): Promise<void> {
  try {
    const response = await fetch(`${getApiUrl()}/public-config`, {
      headers: getNgrokFetchHeaders(),
    });
    if (!response.ok) {
      return;
    }
    const { posthogKey, posthogHost } = (await response.json()) as PublicConfig;
    // Truthy alone wouldn't narrow to string — guard the type so a malformed
    // payload (e.g. a number) can't reach posthog.init().
    if (isStringValue(posthogKey) && posthogKey) {
      // Load the SDK only once we know we'll init it — keeps posthog-js out
      // of the entry chunk (SD-10530) and skips the download entirely when
      // the config has no key.
      const { default: posthog } = await import("posthog-js");
      initPostHog(
        posthog,
        posthogKey,
        isStringValue(posthogHost) && posthogHost
          ? posthogHost
          : DEFAULT_POSTHOG_HOST,
      );
      posthogInstance = posthog;
      // Init landed — wake any gating effects that already ran (and no-op'd)
      // against the not-yet-initialized client so they re-evaluate opt-in/out.
      posthogReady = true;
      for (const listener of readyListeners) {
        listener();
      }
    }
  } catch {
    // Config endpoint unreachable / malformed — leave PostHog uninitialized.
  }
}

const PENDING_LOGIN_KEY = "ph_pending_login_method";
// A deferred login that never gets opted-in (e.g. signing into a disabled
// company) must not resurface as a stale `user_logged_in` if capture is enabled
// much later in the same session. Discard markers older than this window; the
// enabled-company happy path consumes within seconds of login.
const PENDING_LOGIN_TTL_MS = 2 * 60 * 1000;

/**
 * Record that a login just happened (with its method). Because capture is
 * opted-out by default, the login-time `user_logged_in` event would be dropped;
 * usePostHogCompanyGating fires it once it opts in for an enabled company.
 * Survives the client-side post-login redirect via sessionStorage.
 */
export function markPendingLogin(method: string): void {
  try {
    sessionStorage.setItem(
      PENDING_LOGIN_KEY,
      JSON.stringify({ method, ts: Date.now() }),
    );
  } catch {
    // sessionStorage unavailable (SSR / privacy mode) — degrade silently.
  }
}

/** Read and clear the pending-login method, if it exists and is still fresh. */
export function consumePendingLoginMethod(): string | null {
  try {
    const raw = sessionStorage.getItem(PENDING_LOGIN_KEY);
    if (!raw) {
      return null;
    }
    sessionStorage.removeItem(PENDING_LOGIN_KEY);
    const { method, ts } = JSON.parse(raw) as {
      method?: string;
      ts?: number;
    };
    if (
      !method ||
      !isNumberValue(ts) ||
      Date.now() - ts > PENDING_LOGIN_TTL_MS
    ) {
      return null;
    }
    return method;
  } catch {
    return null;
  }
}
